import React from 'react';
import loadable from '@loadable/component';

import { array, bool, func, object, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useLanguage } from '../../context/languageContext';
import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { MIXPANEL_EVENTS, trackMixpanelEvent } from '../../util/mixpanel';

import FallbackPage from './FallbackPage';
import { ASSET_NAME, fetchLandingPageAsset } from './LandingPage.duck';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

// Normalize block titles and listing titles for robust matching
const cleanTitle = title =>
  title
    ? title
      .toLowerCase()
      .replace(/the /g, '')
      .replace(/coastal/g, 'costal')
      .replace(/[^a-z0-9]/g, '')
    : '';

const injectListingImages = (pageData, listings) => {
  if (!pageData || !listings || listings.length === 0) {
    return pageData;
  }

  // Deep clone pageData to prevent mutating Redux state
  const newPageData = JSON.parse(JSON.stringify(pageData));

  // Helper: build a media object from explicit per-breakpoint URLs (used for video-container)
  const buildRawMediaVariants = (matchingListing, desktopUrl, tabletUrl, mobileUrl) => {
    const variants = {};
    if (mobileUrl) {
      variants.mobile = { url: mobileUrl, width: 480, height: 853 };
    }
    if (tabletUrl) {
      variants.tablet = { url: tabletUrl, width: 1024, height: 1365 };
    }
    if (desktopUrl) {
      variants.desktop = { url: desktopUrl, width: 1920, height: 1080 };
    }
    return {
      fieldType: 'image',
      image: {
        id: matchingListing.id,
        type: 'imageAsset',
        attributes: { variants },
      },
    };
  };

  // Find the carousel section by ID
  const section = newPageData.sections?.find(s => s.sectionId === 'nota-a-tour-section');
  if (section && section.blocks) {
    section.blocks = section.blocks.map(block => {
      const blockTitleClean = cleanTitle(block.title?.content);
      if (!blockTitleClean) return block;

      // Find the matching listing
      const matchingListing = listings.find(listing => {
        const listingTitleClean = cleanTitle(listing.attributes?.title);
        return (
          listingTitleClean &&
          (listingTitleClean === blockTitleClean ||
            listingTitleClean.includes(blockTitleClean) ||
            blockTitleClean.includes(listingTitleClean))
        );
      });

      if (matchingListing) {
        const publicData = matchingListing.attributes?.publicData || {};
        const detailsSection = publicData.detailsSection || {};

        let desktopUrl = detailsSection.heroImage?.url;
        let tabletUrl = detailsSection.heroImageTab?.url || desktopUrl;
        let mobileUrl = detailsSection.heroImageMobile?.url || desktopUrl;

        // Fallback to regular listing images if detailsSection doesn't contain hero images
        if (!desktopUrl && matchingListing.images && matchingListing.images.length > 0) {
          const firstImage = matchingListing.images[0];
          const imageVariants = firstImage?.attributes?.variants || {};
          desktopUrl =
            imageVariants['landscape-crop']?.url ||
            imageVariants['default']?.url ||
            Object.values(imageVariants)[0]?.url;
          tabletUrl = desktopUrl;
          mobileUrl = desktopUrl;
        }

        if (desktopUrl) {
          const variants = {};
          if (mobileUrl) {
            variants.mobile = { url: mobileUrl, width: 480, height: 320 };
          }
          if (tabletUrl) {
            variants.tablet = { url: tabletUrl, width: 800, height: 533 };
          }
          if (desktopUrl) {
            variants.desktop = { url: desktopUrl, width: 1200, height: 800 };
          }

          block.media = {
            ...block.media,
            fieldType: 'image',
            image: {
              id: matchingListing.id,
              type: 'imageAsset',
              attributes: {
                variants,
              },
            },
          };
        }
      }
      return block;
    });
  }

  // Inject images for video-container blocks (video-container through video-container-five)
  // across all sections. The block's title is matched against listing titles.
  // The URLs are used exactly as stored in detailsSection — no resizing transformations.
  const VIDEO_CONTAINER_IDS = [
    'video-container',
    'video-container-two',
    'video-container-three',
    'video-container-four',
    'video-container-five',
  ];

  if (newPageData.sections) {
    newPageData.sections.forEach(sec => {
      if (!sec.blocks) return;
      sec.blocks = sec.blocks.map(block => {
        if (!VIDEO_CONTAINER_IDS.includes(block.blockId)) return block;

        const blockTitleClean = cleanTitle(block.title?.content);
        const matchingListing = blockTitleClean
          ? listings.find(listing => {
            const listingTitleClean = cleanTitle(listing.attributes?.title);
            return (
              listingTitleClean &&
              (listingTitleClean === blockTitleClean ||
                listingTitleClean.includes(blockTitleClean) ||
                blockTitleClean.includes(listingTitleClean))
            );
          })
          : null;

        if (!matchingListing) return block;

        const publicData = matchingListing.attributes?.publicData || {};
        const detailsSection = publicData.detailsSection || {};

        const desktopUrl = detailsSection.heroImage?.url;
        const tabletUrl = detailsSection.heroImageTab?.url || desktopUrl;
        const mobileUrl = detailsSection.heroImageMobile?.url || desktopUrl;

        if (!desktopUrl) return block;

        block.media = {
          ...block.media,
          ...buildRawMediaVariants(matchingListing, desktopUrl, tabletUrl, mobileUrl),
        };

        return block;
      });
    });
  }

  return newPageData;
};

export const LandingPageComponent = props => {
  const {
    pageAssetsData,
    inProgress,
    error,
    listings,
    requestedContentLanguage,
    onFetchLandingPageAsset,
  } = props;
  const { currentLanguage } = useLanguage();

  React.useEffect(() => {
    if (!inProgress && requestedContentLanguage !== currentLanguage) {
      onFetchLandingPageAsset(currentLanguage).catch(() => {
        // The hosted-assets state exposes the final fallback error to PageBuilder.
      });
    }
  }, [currentLanguage, inProgress, onFetchLandingPageAsset, requestedContentLanguage]);

  React.useEffect(() => {
    trackMixpanelEvent(MIXPANEL_EVENTS.LANDING_PAGE_VIEWED);

    const trackedDepths = new Set();
    const depthMilestones = [25, 50, 75, 100];
    let maximumScrollDepth = 0;
    let ctaClicked = false;
    let accumulatedActiveMilliseconds = 0;
    let activeStartedAt = document.hidden
      ? null
      : typeof performance !== 'undefined'
        ? performance.now()
        : Date.now();
    let durationTracked = false;

    const pauseActiveTimer = () => {
      if (activeStartedAt === null) {
        return;
      }
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      accumulatedActiveMilliseconds += now - activeStartedAt;
      activeStartedAt = null;
    };
    const resumeActiveTimer = () => {
      if (activeStartedAt === null) {
        activeStartedAt =
          typeof performance !== 'undefined' ? performance.now() : Date.now();
      }
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        pauseActiveTimer();
      } else {
        resumeActiveTimer();
      }
    };
    const trackLandingDuration = exitTrigger => {
      if (durationTracked) {
        return;
      }
      durationTracked = true;
      pauseActiveTimer();
      const durationSeconds = Math.round(accumulatedActiveMilliseconds / 1000);
      trackMixpanelEvent(MIXPANEL_EVENTS.LANDING_PAGE_DURATION, {
        Duration: durationSeconds,
        duration_seconds: durationSeconds,
        exit_trigger: exitTrigger,
        maximum_scroll_depth_percent: maximumScrollDepth,
        cta_clicked: ctaClicked,
      });
    };
    const trackLandingCta = event => {
      const interactiveElement = event.target.closest?.('a, button');
      if (!interactiveElement || interactiveElement.closest('header, footer')) {
        return;
      }
      ctaClicked = true;
      trackMixpanelEvent(MIXPANEL_EVENTS.LANDING_CTA_CLICKED, {
        destination: interactiveElement.getAttribute('href') || null,
        cta_label:
          interactiveElement.textContent?.replace(/\s+/g, ' ').trim() ||
          interactiveElement.getAttribute('aria-label'),
        element_type: interactiveElement.tagName.toLowerCase(),
      });
    };
    const trackScrollDepth = () => {
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollableHeight <= 0) {
        return;
      }
      const currentDepth = Math.min(
        100,
        Math.round((window.scrollY / scrollableHeight) * 100)
      );
      maximumScrollDepth = Math.max(maximumScrollDepth, currentDepth);

      depthMilestones.forEach(depth => {
        if (currentDepth >= depth && !trackedDepths.has(depth)) {
          trackedDepths.add(depth);
          maximumScrollDepth = Math.max(maximumScrollDepth, depth);
          trackMixpanelEvent(MIXPANEL_EVENTS.LANDING_SCROLL_DEPTH, {
            scroll_depth_percent: depth,
          });
        }
      });
    };
    const handlePageHide = () => trackLandingDuration('pagehide');

    window.addEventListener('scroll', trackScrollDepth, { passive: true });
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('click', trackLandingCta);
    trackScrollDepth();
    return () => {
      window.removeEventListener('scroll', trackScrollDepth);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('click', trackLandingCta);
      trackLandingDuration('navigation');
    };
  }, []);

  const rawPageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;
  const processedPageData = React.useMemo(() => {
    return injectListingImages(rawPageData, listings);
  }, [rawPageData, listings]);

  return (
    <PageBuilder
      pageAssetsData={processedPageData}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
  listings: array,
  requestedContentLanguage: string,
  onFetchLandingPageAsset: func.isRequired,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const { listingRefs = [], requestedContentLanguage = null } = state.LandingPage || {};
  const listings = getMarketplaceEntities(state, listingRefs);

  return { pageAssetsData, inProgress, error, listings, requestedContentLanguage };
};

const mapDispatchToProps = dispatch => ({
  onFetchLandingPageAsset: language => dispatch(fetchLandingPageAsset(language)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(connect(mapStateToProps, mapDispatchToProps))(LandingPageComponent);

export default LandingPage;
